package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.AppDialogs
import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.enginetabular.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Img

/** DM-specific clone of ScriptIndexDialog, using the same full Tabular engine. */
@Composable
internal fun LinkedInDmIndex(entries: List<All>, leadTypes: List<VertriebsindexApi.LeadTypEintrag>,
    onChanged: (List<All>) -> Unit, onTypesChanged: (List<VertriebsindexApi.LeadTypEintrag>) -> Unit,
    onClose: () -> Unit) {
    val scope = rememberCoroutineScope()
    var version by remember { mutableStateOf(0) }
    var selected by remember { mutableStateOf<Set<String>>(emptySet()) }
    var drafts by remember { mutableStateOf<List<Pair<String, List<String>>>>(emptyList()) }
    var bulk by remember { mutableStateOf<TabularBulkEditRequest?>(null) }
    var nonce by remember { mutableStateOf(0) }
    var manageTypes by remember { mutableStateOf(false) }
    val kinds = listOf("DM-Nachricht", "Follow-up", "Materialbestätigung")
    val headers = listOf("linkedinDmLeadTypId", "linkedinDmInhalt", "linkedinDmTyp")
    fun data() = entries.map { it.id to listOf(
        if (it.linkedinDmLeadTypId == "__all__") "Alle" else leadTypes.firstOrNull { type -> type.id == it.linkedinDmLeadTypId }?.leadTyp.orEmpty(), it.linkedinDmInhalt,
        if (it.linkedinDmTyp == "DM-Nachricht") "DM" else it.linkedinDmTyp) }
    fun assign(header: String, value: String) { nonce++; bulk = TabularBulkEditRequest(nonce, selected, header, value) }
    fun accept(values: List<All>) { onChanged(values); selected = emptySet(); drafts = emptyList(); bulk = null; version++ }
    if (manageTypes) VertriebLeadtypenDialog(onClose = { manageTypes = false }, onChanged = onTypesChanged, zIndex = POPUP_ENGINE_MODAL_Z_INDEX + 80)
    PopupEngineCard(windowKey = "linkedin-dm-index", zIndex = POPUP_ENGINE_MODAL_Z_INDEX + 40,
        widthPx = 1080, heightPx = 760, minWidthPx = 650, minHeightPx = 400,
        centered = true, viewportAnchored = true, resizable = true, onClose = onClose) {
        Div({ style { width(100.percent); height(100.percent); minHeight(0.px); padding(28.px, 12.px, 12.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
            Div({ attr("role", "heading"); attr("aria-level", "2"); style { textAlign("center"); fontSize(20.px); fontWeight("700"); padding(12.px); marginBottom(12.px); flexShrink(0) } }) { Text("DM Script") }
            key(version) { TabularScreen(
                screenKey = "LinkedInDmIndex", rollenKey = "LinkedInFlow", entityScope = "linkedin-dm-index", title = null,
                headers = headers,
                headerDisplayLabels = headers.map { All.names[it] ?: it }, models = entries,
                data = data(), editable = true, commitChangedRowsOnly = true, includeMetadata = false,
                smartMode = true, multilineColumns = setOf("linkedinDmInhalt"),
                nonEditableCols = setOf("linkedinDmTyp", "linkedinDmLeadTypId"),
                onSelectedRowsChange = { selected = it }, onDraftChange = { drafts = it }, bulkEditRequest = bulk,
                extraNavButtons = listOf(
                    TabularToolbarButton("Lead-Typ", "images/filter.svg", true,
                        dropdownOptions = listOf("__manage__" to "Leadtypen anlegen", "Alle" to "Alle") + leadTypes.map { it.leadTyp to it.leadTyp },
                        dropdownPlaceholder = "Lead-Typ zuweisen", onDropdownChange = {
                            if (it == "__manage__") manageTypes = true
                            else if (it != null && selected.isNotEmpty()) assign("linkedinDmLeadTypId", it)
                        }),
                    TabularToolbarButton("Verwendet in", "images/filter.svg", selected.isNotEmpty(),
                        dropdownOptions = listOf("DM" to "DM", "Follow-up" to "Follow-up", "Materialbestätigung" to "Materialbestätigung"), dropdownPlaceholder = "Verwendet in zuweisen",
                        onDropdownChange = { if (it != null) assign("linkedinDmTyp", it) })
                ),
                createButtonLabel = "DM anlegen", createRow = { listOf("", "", "DM") },
                onRefresh = { scope.launch { try { accept(LinkedInDmApi.index()) } catch (e: CancellationException) { throw e } catch (e: Exception) { AppDialogs.alert(e.message ?: "DM Script konnte nicht geladen werden.") } } },
                onCommit = {}, onCommitConfirmed = { changes, complete -> scope.launch {
                    try {
                        val values = changes.map { (id, cells) ->
                            val kind = cells.getOrNull(2).orEmpty().let { if (it == "DM") "DM-Nachricht" else it }
                            val typeId = if (cells.getOrNull(0) == "Alle") "__all__" else
                                leadTypes.firstOrNull { it.leadTyp == cells.getOrNull(0) }?.id ?: error("Bitte einen vorhandenen Lead-Typ oder Alle zuweisen.")
                            val content = cells.getOrNull(1).orEmpty()
                            require(kind in kinds && content.isNotBlank()) { "Jede Zeile benötigt Typ und Inhalt." }
                            All(id = entries.firstOrNull { it.id == id }?.id.orEmpty(), linkedinDmTyp = kind,
                                linkedinDmLeadTypId = typeId, linkedinDmInhalt = content)
                        }
                        val saved = LinkedInDmApi.saveEntries(values); complete(true); accept(saved)
                    } catch (e: CancellationException) { complete(false); throw e }
                    catch (e: Exception) { complete(false); AppDialogs.alert(e.message ?: "DM-Einträge konnten nicht gespeichert werden.") }
                } },
                onDeleteRows = { ids -> try { accept(LinkedInDmApi.deleteEntries(ids.toSet())) }
                    catch (e: CancellationException) { throw e } catch (e: Exception) { AppDialogs.alert(e.message ?: "DM-Eintrag konnte nicht gelöscht werden."); throw e } }
            ) }
        }
    }
}
